"use client";

/**
 * P3-08 · Quantity stepper — an accessible spinbutton bounded by variant stock.
 *
 * Decrement/increment buttons plus a typed field. The field carries the
 * `spinbutton` role with `aria-valuemin/max/now` so assistive tech announces
 * the bound; arrow keys step; typed entry is clamped to `[1, max]` on change
 * and re-normalised on blur (so a transient empty field doesn't fight the user
 * mid-type). All clamping goes through the shared `clampQuantity`.
 */
import * as React from "react";

import { useTranslator } from "~/i18n/provider";

import { clampQuantity } from "~/lib/cart/cart-interface";
import { cn } from "~/lib/utils";

export function QuantityStepper({
  value,
  max,
  onChange,
  disabled = false,
  className,
}: {
  value: number;
  max: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  const t = useTranslator();
  // Local text mirror so the user can clear the field while typing; the
  // committed numeric value stays clamped via onChange.
  const [text, setText] = React.useState(String(value));
  React.useEffect(() => setText(String(value)), [value]);

  const commit = (raw: number) => {
    const next = clampQuantity(raw, max);
    if (next >= 1) onChange(next);
  };

  const atMin = value <= 1;
  const atMax = value >= max;

  const btn =
    "flex size-10 items-center justify-center rounded-[var(--btn-radius)] border border-border text-fg transition-colors hover:border-fg disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div
      className={cn("inline-flex items-center gap-1", className)}
      data-testid="pdp-qty"
    >
      <button
        type="button"
        aria-label={t("pdp.decreaseQuantity")}
        disabled={disabled || atMin}
        onClick={() => commit(value - 1)}
        className={btn}
      >
        <span aria-hidden>−</span>
      </button>

      <input
        type="text"
        inputMode="numeric"
        role="spinbutton"
        aria-label={t("pdp.quantity")}
        aria-valuemin={1}
        aria-valuemax={max}
        aria-valuenow={value}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const parsed = Number(raw);
          if (raw.trim() !== "" && Number.isFinite(parsed)) commit(parsed);
        }}
        onBlur={() => {
          // Re-normalise on blur so an empty/invalid field snaps back to value.
          commit(Number(text));
          setText(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") {
            e.preventDefault();
            commit(value + 1);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            commit(value - 1);
          }
        }}
        className="h-10 w-14 rounded-[var(--btn-radius)] border border-border bg-surface text-center text-sm text-fg [appearance:textfield] focus:border-fg focus:outline-none disabled:opacity-40"
      />

      <button
        type="button"
        aria-label={t("pdp.increaseQuantity")}
        disabled={disabled || atMax}
        onClick={() => commit(value + 1)}
        className={btn}
      >
        <span aria-hidden>+</span>
      </button>
    </div>
  );
}
